import { Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative, TAdGroup } from '@feathr/blackbox';
import { Checkbox, FormElement, Well } from '@feathr/components';

import * as styles from '../CreativeForm.css';

interface ITargetingTabProps {
  creative: DisplayCreative;
}

function TargetingTab({ creative }: Readonly<ITargetingTabProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const adGroups = creative.get('ad_groups');
  const isAllSelected = Array.isArray(adGroups) && adGroups.length === 0;
  const hasMain = Array.isArray(adGroups) && adGroups.includes('main');
  const hasLookalike = Array.isArray(adGroups) && adGroups.includes('lookalike');
  const isPartial = !isAllSelected && (hasMain || hasLookalike);

  function getAllCheckboxValue(): boolean | 'indeterminate' {
    if (isAllSelected) {
      return true;
    }
    if (isPartial) {
      return 'indeterminate';
    }
    return false;
  }

  function handleAllChange(checked?: boolean): void {
    if (checked) {
      creative.set({ ad_groups: [] as TAdGroup[] });
    } else {
      creative.set({ ad_groups: null });
    }
  }

  function handleGroupChange(group: TAdGroup, checked?: boolean): void {
    const current = Array.isArray(adGroups) ? [...adGroups] : [];
    if (isAllSelected) {
      if (checked) {
        return;
      }
      const other: TAdGroup = group === 'main' ? 'lookalike' : 'main';
      creative.set({ ad_groups: [other] });
      return;
    }

    if (checked) {
      if (!current.includes(group)) {
        current.push(group);
      }
    } else {
      const idx = current.indexOf(group);
      if (idx >= 0) {
        current.splice(idx, 1);
      }
    }
    if (current.length === 2) {
      creative.set({ ad_groups: [] as TAdGroup[] });
    } else if (current.length === 0) {
      creative.set({ ad_groups: null });
    } else {
      creative.set({ ad_groups: current });
    }
  }

  return (
    <Stack gap={theme.spacing['4']}>
      <FormElement
        helpText={t('Choose which audiences this creative should target')}
        label={t('Audience Targeting')}
      >
        <Well layout={'vertical'} name={'audience-target-checkboxes'}>
          <Stack gap={theme.spacing['3']}>
            <Checkbox
              label={t('All available audiences')}
              onChange={handleAllChange}
              value={getAllCheckboxValue()}
            />
            <hr className={styles.divider} />
            <Checkbox
              label={t('Targeted groups')}
              onChange={(checked) => handleGroupChange('main', checked)}
              value={isAllSelected || hasMain}
            />
            <Checkbox
              label={t('Lookalike audiences')}
              onChange={(checked) => handleGroupChange('lookalike', checked)}
              value={isAllSelected || hasLookalike}
            />
          </Stack>
        </Well>
      </FormElement>
    </Stack>
  );
}

export default observer(TargetingTab);
